import type { App } from '@grafikr/components';

import 'layout/tables';

import cartCount from 'layout/cart-count';
import header from 'layout/header';

import geoPopup from 'components/geo-popup';

import hasUrlParam from 'utils/loaders/has-url-param';
import intersectionLoader from 'utils/loaders/intersection';
import onElementInteraction from 'utils/loaders/on-element-interaction';
import onEvent from 'utils/loaders/on-event';
import onKey from 'utils/loaders/on-key';

export default (app: App) => {
  app.add({
    // Layout
    'cart-count': cartCount,
    header,
    'geo-popup': geoPopup,
    'navigation-drawer': [
      onElementInteraction,
      () => import(/* webpackChunkName: "layout-navigation-drawer" */ 'layout/navigation-drawer'),
    ],
    filter: [
      intersectionLoader,
      () => import(/* webpachChunkName: "layout-filter" */ 'layout/filter'),
    ],
    'filter-drawer': [
      onElementInteraction,
      () => import(/* webpachChunkName: "layout-filter-drawer" */ 'layout/filter-drawer'),
    ],

    // Components
    range: [
      intersectionLoader,
      () => import(/* webpackChunkname: "component-range" */ 'components/range'),
    ],
    'address-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-address-form" */ 'components/address-form'),
    ],
    'cart-drawer': [
      [onEvent, onElementInteraction, hasUrlParam],
      () => import(/* webpackChunkName: "component-cart-drawer" */ 'components/cart-drawer'),
    ],
    'country-drawer': [
      onElementInteraction,
      () => import(/* webpackChunkName: "component-country-drawer" */ 'components/country-drawer'),
    ],
    'search-suggestions': [
      [onKey, onElementInteraction],
      () =>
        import(
          /* webpackChunkName: "component-search-suggestions" */ 'components/search-suggestions'
        ),
    ],
    copy: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-copy" */ 'components/copy'),
    ],
    'lazy-content': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-lazy-content" */ 'components/lazy-content'),
    ],
    pagination: [
      [intersectionLoader, 'filter:update'],
      () => import(/* webpackChunkName: "component-pagination" */ 'components/pagination'),
    ],
    hero: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-hero" */ 'components/hero'),
    ],
    carousel: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-carousel" */ 'components/carousel'),
    ],
    video: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-video" */ 'components/video'),
    ],
    collapsible: [
      'click',
      () => import(/* webpackChunkName: "component-collapsible" */ 'components/collapsible'),
    ],
    reveal: [
      onElementInteraction,
      () => import(/* webpackChunkName: "component-reveal" */ 'components/reveal'),
    ],
    'announcement-bar': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "component-announcement-bar" */ 'components/announcement-bar'),
    ],
    script: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-script" */ 'components/script'),
    ],
    'linked-swatches': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "component-linked-swatches" */ 'components/linked-swatches'),
    ],
    'size-right': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "component-size-right" */ 'components/size-right'),
    ],
    'feed-filter': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-feed-filter" */ 'components/feed-filter'),
    ],
    'usp-slider': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-usp-slider" */ 'components/usp-slider'),
    ],
    'product-wishlist-button': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-product-wishlist-button" */ 'components/product-wishlist-button'),
    ],
    'wishlist-count': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-wishlist-count" */ 'components/wishlist-count'),
    ],

    // Pages
    'create-customer': [
      intersectionLoader,
      () => import(/* webpackChunkName: "customer-create" */ 'pages/customers/create'),
    ],

    collection: [
      intersectionLoader,
      () => import(/* webpackChunkName: "collection-main" */ 'pages/collection/main'),
    ],
    'collection-subcollections': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "collection-subcollections" */ 'pages/collection/subcollections'
        ),
    ],

    product: [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-main" */ 'pages/product/main'),
    ],
    'product-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-form" */ 'pages/product/form'),
    ],
    'product-option': [
      onElementInteraction,
      () => import(/* webpackChunkName: "product-option" */ 'pages/product/option'),
    ],
    'product-recommendations': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "product-recommendations" */ 'pages/product/recommendations'),
    ],
    'product-lightbox': [
      onElementInteraction,
      () => import(/* webpackChunkName: "product-lightbox" */ 'pages/product/lightbox'),
    ],
    'product-sizeguide': [
      onElementInteraction,
      () => import(/* webpackChunkName: "product-sizeguide" */ 'pages/product/sizeguide-drawer'),
    ],
    'product-upsell': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-upsell" */ 'pages/product/upsell'),
    ],
    'product-linked': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-linked" */ 'pages/product/linked'),
    ],
    'product-policy-drawer': [
      onElementInteraction,
      () => import(/* webpackChunkName: "product-policy-drawer" */ 'pages/product/policy-drawer'),
    ],
    'product-gallery': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-gallery" */ 'pages/product/gallery'),
    ],
    'product-gallery-container': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-gallery" */ 'pages/product/gallery-container'),
    ],
    wishlist: [
      intersectionLoader,
      () => import(/* webpackChunkName: "wishlist-main" */ 'pages/wishlist/main'),
    ]
  });
};
